import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputLabel,
  Fade,
} from "@mui/material";
import { Link, useNavigate } from "react-router";
import BrandCarousel from "../../login/components/BrandCarousel";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import PasswordField from "../../../components/PasswordField";
import { resetState, signup } from "../../../redux/slices/loginSlice";

const SignupForm = () => {
  const [fadeIn, setFadeIn] = useState(false);

  const { loading, signUpSuccess, signUpError } = useAppSelector(
    (state) => state.login,
  ) as any;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setFadeIn(true);

    return () => {
      // Reset the state when the component unmounts
      dispatch(resetState());
    };
  }, [dispatch, setFadeIn]);

  const handleSignup = useCallback(
    (e: any) => {
      e.preventDefault();
      const email = e.target.email.value;
      const password = e.target.password.value || "";
      const confirmPassword = e.target.confirmPassword.value || "";
      const first = e.target.first.value;
      const last = e.target.last.value;
      const role = "client";

      dispatch(
        signup({
          email,
          password,
          confirmPassword,
          name: { first, last },
          role,
        }),
      );
    },
    [dispatch],
  );

  const handleBackToLogin = useCallback(
    (e: any) => {
      e.preventDefault();

      setFadeIn(false);

      dispatch(resetState());

      setTimeout(() => {
        navigate("/login");
      }, 1000);
    },
    [dispatch, navigate, setFadeIn],
  );

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", p: 2 }}
        >
          <Box
            component="form"
            onSubmit={handleSignup}
            sx={{
              maxWidth: 350,
              mx: "auto",
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
              },
            }}
          >
            <BrandCarousel />
            <Typography variant="h4" sx={{ fontSize: 24 }} gutterBottom>
              Create Your Account
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 15, mb: 2 }}
              gutterBottom
            >
              Password is optional. You can use your email to login with Secure
              Link.
            </Typography>

            <InputLabel required htmlFor="email" sx={{ fontSize: 14 }}>
              Email Address
            </InputLabel>
            <TextField
              id="email"
              variant="outlined"
              size="small"
              required
              fullWidth
              margin="normal"
              autoComplete="email"
              disabled={loading || signUpSuccess}
              error={signUpError?.toLowerCase()?.includes("email")}
              placeholder="Enter your email address"
            />
            <InputLabel htmlFor="password" sx={{ fontSize: 14 }}>
              Password
            </InputLabel>
            <PasswordField
              id="password"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="current-password"
              disabled={loading || signUpSuccess}
              error={signUpError?.toLowerCase()?.includes("password")}
              placeholder="Enter your password"
            />
            <InputLabel htmlFor="confirmPassword" sx={{ fontSize: 14 }}>
              Confirm Password
            </InputLabel>
            <PasswordField
              id="confirmPassword"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="current-password"
              disabled={loading || signUpSuccess}
              error={signUpError?.toLowerCase()?.includes("password")}
              placeholder="Confirm your password"
            />
            <InputLabel required htmlFor="first" sx={{ fontSize: 14 }}>
              First Name
            </InputLabel>
            <TextField
              id="first"
              type="text"
              required
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              disabled={loading || signUpSuccess}
              error={signUpError?.toLowerCase()?.includes("name")}
              placeholder="Enter your first name"
            />
            <InputLabel required htmlFor="last" sx={{ fontSize: 14 }}>
              Last Name
            </InputLabel>
            <TextField
              id="last"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              required
              margin="normal"
              disabled={loading || signUpSuccess}
              error={signUpError?.toLowerCase()?.includes("name")}
              placeholder="Enter your last name"
            />

            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              disabled={loading || signUpSuccess}
            >
              Create account
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ mt: 2, fontSize: 14 }}
              >
                Already have an account?{" "}
                <Link
                  onClick={handleBackToLogin}
                  to="/login"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: "bold",
                    fontFamily: "Brandon_Grotesque_bold",
                  }}
                >
                  Log In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{
            display: { xs: "none", md: "block" },
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
              src="/signup_img.jpg"
              alt="Logo"
            />
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default SignupForm;
